'use client'

/**
 * SceneImage — an <img> that shows the standard animated skeleton (a soft grey
 * pulse) while it decodes, then fades the image in.
 *
 * Originally extracted from the media-tab scene template (removed in CAS
 * migration PR-9); now used by the annotated-image block in
 * `portable-text-renderer.tsx`.
 *
 * The skeleton fills the wrapper, so the wrapper must carry the box dimensions
 * (a fixed aspect, or a `loadingClassName` min-height for native-sized images).
 * When `src` can change (annotation toggle, step index), pass a `key={src}` at
 * the call site to re-arm the skeleton — or omit it to swap the source in place
 * (the browser holds the previous frame until the new one decodes).
 *
 * `onStatusChange` lets a caller that wraps the image in its own box react to
 * the skeleton clearing — e.g. the Scene tab's photo, whose overlay anchor is
 * `w-fit` (hugs the decoded image) but must be full-width while the skeleton
 * shows (an undecoded <img> has no intrinsic size to hug) and must not shrink
 * onto a broken-image icon if the load fails.
 */

import { useCallback, useEffect, useState } from 'react'

/** `loading` → skeleton; `loaded` → decoded; `error` → the load failed (the
 *  skeleton still clears, so the browser's broken-image / alt text shows). */
export type SceneImageStatus = 'loading' | 'loaded' | 'error'

export function SceneImage({
  src,
  alt,
  wrapperClassName = '',
  imgClassName = '',
  loadingClassName = '',
  fallback = null,
  onStatusChange,
  ariaHidden = false,
  imgStyle,
}: {
  src: string | null | undefined
  alt: string
  wrapperClassName?: string
  imgClassName?: string
  /** classes applied to the wrapper only while loading (e.g. a min-height) */
  loadingClassName?: string
  /** rendered (centered on a neutral grey box) when there is no `src` */
  fallback?: React.ReactNode
  /** notified with the initial `loading` and every change after it.
   *  Pass a stable function (e.g. a state setter); it's an effect dependency. */
  onStatusChange?: (status: SceneImageStatus) => void
  /** hide the whole image (wrapper included) from assistive tech — for a
   *  decorative / visually-hidden copy, e.g. an inactive stacked Scene layer */
  ariaHidden?: boolean
  /** inline style on the <img> (e.g. a settings-driven max-height) */
  imgStyle?: React.CSSProperties
}) {
  const [status, setStatus] = useState<SceneImageStatus>('loading')
  const loaded = status !== 'loading'

  // A server-rendered image can finish loading — or fail — before React
  // hydrates and attaches onLoad/onError, which would leave the skeleton stuck
  // forever. This callback ref checks `complete` at mount (true once the image
  // is decoded OR broken; naturalWidth tells the two apart) and settles at once.
  const imgRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete) setStatus(node.naturalWidth > 0 ? 'loaded' : 'error')
  }, [])

  useEffect(() => {
    onStatusChange?.(status)
  }, [status, onStatusChange])

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-muted ${wrapperClassName}`}>
        {fallback}
      </div>
    )
  }

  return (
    <div
      aria-hidden={ariaHidden || undefined}
      className={`relative ${wrapperClassName} ${loaded ? '' : loadingClassName}`}
    >
      {!loaded && <span aria-hidden className="absolute inset-0 animate-pulse bg-muted" />}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
        className={`${imgClassName} ${loaded ? '' : 'invisible'}`}
        style={imgStyle}
      />
    </div>
  )
}
